/**
 * LeadEmailDialog — compose & send an email to a single CRM lead.
 *
 * Uses the existing TipTap `RichTextEditor` for the body and posts a
 * multipart payload to `POST /api/crm/leads/{lead_id}/send-email`.
 *
 * After send, surfaces the live tracking timeline (sent → delivered →
 * opened → clicked) by polling `GET /api/crm/leads/{lead_id}/emails`
 * every 8 seconds while the dialog is open.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, Send, Paperclip, X, Check, Eye, MousePointerClick, AlertCircle, Clock, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import RichTextEditor from '../RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('savenes_token')}` });

const STATUS_META = {
  queued:    { label: 'Queued',    color: '#64748B', Icon: Clock },
  sent:      { label: 'Sent',      color: '#0E7490', Icon: Send },
  delivered: { label: 'Delivered', color: '#166534', Icon: Check },
  opened:    { label: 'Opened',    color: '#7C3AED', Icon: Eye },
  clicked:   { label: 'Clicked',   color: '#1A56DB', Icon: MousePointerClick },
  bounced:   { label: 'Bounced',   color: '#991B1B', Icon: AlertCircle },
  failed:    { label: 'Failed',    color: '#991B1B', Icon: AlertCircle },
};

function fmtTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function LeadEmailDialog({ open, onOpenChange, lead }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [pastEmails, setPastEmails] = useState([]);
  const [pollLoading, setPollLoading] = useState(false);
  const pollTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Reset on open / clean up on close
  useEffect(() => {
    if (open && lead?.id) {
      setSubject('');
      setBody('');
      setCc('');
      setBcc('');
      setAttachments([]);
      loadEmails();
      // Poll every 8s while open so users see tracking updates live.
      pollTimerRef.current = setInterval(loadEmails, 8000);
    } else {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    }
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead?.id]);

  const loadEmails = async () => {
    if (!lead?.id) return;
    setPollLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/crm/leads/${lead.id}/emails`, { headers: auth() });
      setPastEmails(r.data.emails || []);
    } catch { /* silently — caller chose to ignore */ }
    finally { setPollLoading(false); }
  };

  const totalAttachmentSize = useMemo(() => attachments.reduce((s, f) => s + f.size, 0), [attachments]);

  const handleFiles = (files) => {
    const incoming = Array.from(files || []);
    const valid = [];
    let running = totalAttachmentSize;
    for (const f of incoming) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} is over 8 MB`); continue; }
      if (running + f.size > 20 * 1024 * 1024) { toast.error('Total attachments exceed 20 MB'); break; }
      running += f.size;
      valid.push(f);
    }
    setAttachments([...attachments, ...valid]);
  };

  const removeAttachment = (idx) => setAttachments(attachments.filter((_, i) => i !== idx));

  const send = async () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body || body === '<p></p>') { toast.error('Email body is empty'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('subject', subject);
      fd.append('html', body);
      if (cc.trim()) fd.append('cc', cc.trim());
      if (bcc.trim()) fd.append('bcc', bcc.trim());
      for (const f of attachments) fd.append('attachments', f, f.name);
      const r = await axios.post(`${API_URL}/api/crm/leads/${lead.id}/send-email`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success(`Email sent to ${lead.email}`);
      setSubject(''); setBody(''); setCc(''); setBcc(''); setAttachments([]);
      setPastEmails([r.data, ...pastEmails]);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to send email');
    } finally { setSending(false); }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (sending) return; onOpenChange(v); }}>
      <DialogContent className="max-w-3xl" data-testid="lead-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2342]">
            <Mail className="w-5 h-5 text-[#0E7490]" />
            Email {lead.name}
            {lead.email ? (
              <span className="text-xs font-normal text-[#64748B]">· {lead.email}</span>
            ) : (
              <span className="text-xs font-normal text-[#991B1B]">· No email on file</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {!lead.email ? (
          <div className="text-sm text-[#991B1B] py-6 text-center">This lead has no email address. Add one in the Edit Lead form.</div>
        ) : (
          <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4 max-h-[70vh] overflow-y-auto">
            {/* Compose */}
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-[#64748B]">Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" className="h-9 text-sm" data-testid="email-subject-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-[#64748B]">Cc (optional)</Label>
                  <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="a@b.com, c@d.com" className="h-9 text-xs" data-testid="email-cc-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#64748B]">Bcc (optional)</Label>
                  <Input value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="hidden@d.com" className="h-9 text-xs" data-testid="email-bcc-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#64748B]">Body</Label>
                <RichTextEditor value={body} onChange={setBody} placeholder="Hi {{first_name}},…" />
              </div>

              {/* Attachments */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs text-[#64748B] flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Attachments</Label>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fileInputRef.current?.click()} data-testid="email-attach-btn">
                    <Paperclip className="w-3.5 h-3.5 mr-1.5" /> Add files
                  </Button>
                  <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    className="hidden"
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
                    data-testid="email-file-input"
                  />
                </div>
                {attachments.length === 0 ? (
                  <p className="text-[11px] text-[#94A3B8]">No attachments. Max 8 MB per file, 20 MB total.</p>
                ) : (
                  <ul className="space-y-1">
                    {attachments.map((f, i) => (
                      <li key={i} className="flex items-center justify-between text-xs bg-[#F8FAFC] border border-[#E2E8F0] rounded px-2 py-1.5" data-testid={`email-attachment-${i}`}>
                        <span className="truncate text-[#0A2342]">{f.name} <span className="text-[#94A3B8]">· {(f.size / 1024).toFixed(0)} KB</span></span>
                        <button onClick={() => removeAttachment(i)} className="text-[#991B1B] hover:bg-[#991B1B]/10 p-1 rounded"><X className="w-3 h-3" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex justify-end pt-1 border-t border-[#F1F5F9]">
                <Button onClick={send} disabled={sending} className="careiq-btn-primary" data-testid="email-send-btn">
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {sending ? 'Sending…' : 'Send email'}
                </Button>
              </div>
            </div>

            {/* Tracking */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-[#0A2342]">Email history</div>
                <button onClick={loadEmails} className="text-[#64748B] hover:text-[#0E7490]" data-testid="email-refresh-btn">
                  {pollLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </button>
              </div>
              {pastEmails.length === 0 ? (
                <div className="text-xs text-[#94A3B8] py-4 text-center">No prior emails to this lead.</div>
              ) : (
                <ul className="space-y-2">
                  {pastEmails.map(e => <EmailTimelineRow key={e.id} email={e} />)}
                </ul>
              )}
              <p className="text-[10px] text-[#94A3B8] mt-3 leading-snug">
                Delivered, opened, and clicked status is updated via Resend webhooks once configured (Resend dashboard → Webhooks → <code className="bg-[#F1F5F9] px-1 rounded">/api/webhooks/resend</code>).
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EmailTimelineRow({ email }) {
  const meta = STATUS_META[email.status] || STATUS_META.queued;
  const Icon = meta.Icon;
  return (
    <li className="border border-[#E2E8F0] rounded-lg p-2.5 text-xs" data-testid={`email-history-${email.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-[#0A2342] truncate">{email.subject}</div>
          <div className="text-[10px] text-[#94A3B8]">{fmtTime(email.sent_at || email.created_at)}</div>
        </div>
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0" style={{ backgroundColor: meta.color + '15', color: meta.color }}>
          <Icon className="w-3 h-3" /> {meta.label}
          {email.opened_count > 1 ? <span> ×{email.opened_count}</span> : null}
        </span>
      </div>
      {/* Step rail */}
      <div className="flex items-center gap-1 mt-2 flex-wrap">
        <Step ok={!!email.sent_at} label="Sent" at={email.sent_at} />
        <Step ok={!!email.delivered_at} label="Delivered" at={email.delivered_at} />
        <Step ok={!!email.opened_at} label="Opened" at={email.opened_at} />
        <Step ok={!!email.clicked_at} label="Clicked" at={email.clicked_at} />
        {email.bounced_at && <span className="text-[10px] text-[#991B1B]">· Bounced: {email.bounce_reason || ''}</span>}
        {email.status === 'failed' && <span className="text-[10px] text-[#991B1B]">· {email.error || 'Failed'}</span>}
      </div>
      {(email.attachments || []).length > 0 && (
        <div className="text-[10px] text-[#94A3B8] mt-1.5">
          <Paperclip className="w-2.5 h-2.5 inline mr-0.5" />
          {email.attachments.length} attachment{email.attachments.length === 1 ? '' : 's'}
        </div>
      )}
    </li>
  );
}

function Step({ ok, label, at }) {
  return (
    <span
      title={at ? `${label} · ${fmtTime(at)}` : `${label} — pending`}
      className={`inline-flex items-center gap-0.5 text-[10px] ${ok ? 'text-[#0E7490] font-medium' : 'text-[#CBD5E1]'}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-[#0E7490]' : 'bg-[#CBD5E1]'}`} />
      {label}
    </span>
  );
}
